import type { PaginatedResponse } from '@hygge/shared';

/** The page size every all-pages read asks for. */
export const ALL_PAGES_PAGE_SIZE = 100;

/**
 * Every row of a paginated list, not just the first page.
 *
 * A list endpoint returns one page, so a window with more than `limit` rows
 * hides the rest — for the time-entry lists, which sort latest-first, the
 * earliest ones. A screen that sums the rows it holds —
 * the Weekly Timesheet's day totals [HW-869], Team Time's per-person and
 * per-project totals [HW-930] — turns a missing page into a wrong total, not
 * merely a hidden row. A window is small — one request as a rule, a handful at
 * worst — so the remaining pages are fetched by the count the server reports
 * and concatenated, rather than the limit raised to a number a heavier window
 * could still slip past.
 */
export async function collectAllPages<T extends { id: string }>(
  fetchPage: (page: number, limit: number) => Promise<PaginatedResponse<T>>,
): Promise<PaginatedResponse<T>> {
  const first = await fetchPage(1, ALL_PAGES_PAGE_SIZE);
  if (first.meta.totalPages <= 1) return first;

  const rest = await Promise.all(
    Array.from({ length: first.meta.totalPages - 1 }, (_, i) =>
      fetchPage(i + 2, ALL_PAGES_PAGE_SIZE),
    ),
  );

  // Dedupe by id. A row added between the first page and these, anywhere
  // before a page boundary in the sort order, lands the offset windows one row
  // over, so a row can arrive on two pages; keeping the first occurrence drops
  // the duplicate rather than double-counting it in a total or drawing two rows
  // under one React key. A row DELETED in that gap shifts the windows the
  // other way and one row is not fetched at all. Nothing here can see that;
  // the next read is whole — at once when the delete came from this client,
  // whose invalidation refetches, otherwise at the next refetch.
  const seen = new Set<string>();
  const data: T[] = [];
  for (const row of [...first.data, ...rest.flatMap((page) => page.data)]) {
    if (!seen.has(row.id)) {
      seen.add(row.id);
      data.push(row);
    }
  }

  // One coherent page describing exactly what was gathered, so `meta` never
  // contradicts `data` for a caller that reads it.
  return { data, meta: { total: data.length, page: 1, limit: data.length, totalPages: 1 } };
}
